import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Modal,
  Fade,
  Backdrop,
  Alert,
  TextField,
  Snackbar,
  IconButton
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SendIcon from '@mui/icons-material/Send'; // Import the Send Icon
import { acquireToken } from './auth';
import { useLocalStorageState } from './useLocalStorageState'; // Adjust path if needed


// --- React Component ---

const App = () => {
  const [themeMode, setThemeMode] = useLocalStorageState('themeMode', 'dark');
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false); // NEW: State for test email button
  const [successMessage, setSuccessMessage] = useState(null);

  // State for the snooze modal
  const [snoozeModalOpen, setSnoozeModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [snoozeDateTime, setSnoozeDateTime] = useState('');

  const theme = useMemo(
  () =>
    createTheme({
      palette: {
        mode: themeMode,
        ...(themeMode === 'light'
          ? {
              // Palette values for light mode
              primary: { main: '#008AAB' },
              background: { default: '#f4f6f8', paper: '#ffffff' },
            }
          : {
              // Palette values for dark mode
              primary: { main: '#008aab' },
              background: { default: '#121212', paper: '#202020ff' },
            }),
      },
      typography: {
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    }),
  [themeMode],
);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: 'background.paper',
    border: '1px solid #555',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await acquireToken();
      if (!token) {
        setError("Authentication failed. Could not acquire token.");
        return;
      }

      const response = await fetch('https://questodmmonitoringmanager-bsb8edgae0d4avaz.westus2-01.azurewebsites.net/api/Get-TableData', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error ${response.status}: ${errorText}`);
      }
      const data = await response.json();
      setWorkflows(data);
    } catch (e) {
      setError(`Failed to load workflows: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const handleCheckForNew = async () => {
    setIsUpdating(true);
    setError(null);
    try {
      const token = await acquireToken();
      if (!token) {
        setError("Authentication failed. Could not acquire token.");
        return;
      }

      const response = await fetch('https://questodmmonitoringmanager-bsb8edgae0d4avaz.westus2-01.azurewebsites.net/api/Get-Workflows', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error ${response.status}: ${errorText}`);
      }
      setSuccessMessage("Successfully triggered workflow update. Refreshing table...");
      await loadWorkflows();
    } catch (e) {
      setError(`Failed to trigger workflow update: ${e.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // NEW: Function to send the test alert email
  const handleSendTestEmail = async () => {
    setIsSendingTest(true);
    setError(null);
    
    // Test payload as requested
    const testPayload = {
      region: "us",
      orgid: "00000000-0000-0000-0000-000000000000",
      customerFilter: "TEST_FAILURE_CUSTOMER_NAME"
    };

    try {
      // NOTE: We don't acquire a token here as the target function URL includes a code query parameter, 
      // suggesting it uses an API key/function code for authentication, not the user's Entra ID token.
      
      const response = await fetch('https://questodmlogcollectionfunction-bdche9h3gucvgyfh.westus2-01.azurewebsites.net/api/QuestODMLogCollector?code=byd1XtNqCXBmkxA7KT0_51iO3cudP4NgNrYJ6wjH1QlyAzFuuQcfig==', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error ${response.status}: ${errorText}`);
      }
      
      setSuccessMessage("Test failure ticket request successfully sent.");
    } catch (e) {
      setError(`Failed to send test ticket: ${e.message}`);
    } finally {
      setIsSendingTest(false);
    }
  };

  // --- Snooze Functionality ---

  // Helper to format date for datetime-local input
  const toDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const openSnoozeModal = (workflow) => {
    setSelectedWorkflow(workflow);
    const defaultSnoozeDate = new Date();
    defaultSnoozeDate.setHours(defaultSnoozeDate.getHours() + 1); // Default to 1 hour from now
    setSnoozeDateTime(toDateTimeLocal(defaultSnoozeDate));
    setSnoozeModalOpen(true);
  };

  const closeSnoozeModal = () => {
    setSnoozeModalOpen(false);
    setSelectedWorkflow(null);
  };
  
  const setSnoozeForHours = (hours) => {
    const newDate = new Date();
    newDate.setHours(newDate.getHours() + hours);
    setSnoozeDateTime(toDateTimeLocal(newDate));
  };

  const updateSnoozeState = async (workflow, snoozeUntil) => {
    const { PartitionKey: Region, RowKey: WorkflowID } = workflow;
    setError(null);

    // Optimistically update the UI for a responsive feel
    setWorkflows(prevWorkflows =>
      prevWorkflows.map(wf =>
        (wf.PartitionKey === Region && wf.RowKey === WorkflowID) ? { ...wf, SnoozedUntil: snoozeUntil } : wf
      )
    );

    try {
      const token = await acquireToken();
      if (!token) {
        throw new Error("Authentication failed. Could not acquire token.");
      }
      
      const response = await fetch('https://questodmmonitoringmanager-bsb8edgae0d4avaz.westus2-01.azurewebsites.net/api/Update-SnoozeState', { // NOTE: Update this URL if your function has a different name
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          Region,
          WorkflowID,
          SnoozeUntil: snoozeUntil, // This can be an ISO string or null
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error ${response.status}: ${errorText}`);
      }

      setSuccessMessage(`Workflow '${workflow.WorkflowName}' snooze state updated.`);

    } catch (e) {
      setError(`Failed to update snooze state: ${e.message}`);
      // On failure, revert the optimistic update by reloading data from the server
      loadWorkflows();
    }
  };

  const handleSnoozeConfirm = async () => {
    if (!selectedWorkflow || !snoozeDateTime) return;

    // The 'datetime-local' input gives a string like "YYYY-MM-DDTHH:mm"
    // new Date() correctly parses this into a local time date object.
    // .toISOString() converts it to the UTC string the backend expects.
    const snoozeUntilUTC = new Date(snoozeDateTime).toISOString();

    await updateSnoozeState(selectedWorkflow, snoozeUntilUTC);
    closeSnoozeModal();
  };

  const handleUnsnooze = async (workflow) => {
    await updateSnoozeState(workflow, null);
  };

  const formatSnoozeDate = (dateString) => {
    if (!dateString) return 'Not Snoozed';
    return new Date(dateString).toLocaleString(); // Displays in user's local timezone
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <img
            src="/g.svg"
            alt="CloudWave Logo"
            style={{ height: '40px', marginRight: '16px' }}></img>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            BinaryTree ODM Monitoring Manager
          </Typography>
          <IconButton sx={{ ml: 1 }} onClick={() => setThemeMode(themeMode === 'light' ? 'dark' : 'light')} color="inherit">
            {themeMode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Monitored Workflows</Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* NEW: Send Test Email Button */}
            <Button 
                variant="outlined" 
                color="secondary" // Use a distinct color like secondary or error
                onClick={handleSendTestEmail} 
                disabled={isSendingTest}
                startIcon={isSendingTest ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            >
                {isSendingTest ? 'Sending Test...' : 'Send Test Ticket'}
            </Button>

            <Button variant="contained" color="primary" onClick={handleCheckForNew} disabled={isUpdating || loading}>
              {isUpdating ? <CircularProgress size={24} color="inherit" /> : 'Update Workflows'}
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="workflow monitor table">
            <TableHead>
              <TableRow>
                <TableCell>Workflow Name</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>OrgID</TableCell>
                <TableCell>Snoozed Until</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 1 }}>Loading Workflows...</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                workflows.map((wf) => (
                  <TableRow key={wf.RowKey} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">{wf.WorkflowName}</TableCell>
                    <TableCell>{wf.PartitionKey}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{wf.OrgID}</TableCell>
                    <TableCell>{formatSnoozeDate(wf.SnoozedUntil)}</TableCell>
                    <TableCell align="center">
                      {wf.SnoozedUntil ? (
                        <Button variant="outlined" color="secondary" size="small" onClick={() => handleUnsnooze(wf)}>
                          Unsnooze
                        </Button>
                      ) : (
                        <Button variant="outlined" color="primary" size="small" onClick={() => openSnoozeModal(wf)}>
                          Snooze
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />

      {/* Snooze Date Picker Modal */}
      <Modal
        open={snoozeModalOpen}
        onClose={closeSnoozeModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={snoozeModalOpen}>
          <Box sx={modalStyle}>
            <Typography variant="h6" component="h2">
              Snooze Workflow
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, fontFamily: 'monospace' }}>
              {selectedWorkflow?.WorkflowName}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button size="small" variant="outlined" onClick={() => setSnoozeForHours(1)}>+1 Hour</Button>
                <Button size="small" variant="outlined" onClick={() => setSnoozeForHours(6)}>+6 Hours</Button>
                <Button size="small" variant="outlined" onClick={() => setSnoozeForHours(24)}>+24 Hours</Button>
            </Box>

            <TextField
              id="datetime-local"
              label="Snooze Until"
              type="datetime-local"
              fullWidth
              value={snoozeDateTime}
              onChange={(e) => setSnoozeDateTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={closeSnoozeModal}>Cancel</Button>
              <Button variant="contained" onClick={handleSnoozeConfirm}>Confirm Snooze</Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </ThemeProvider>
  );
};

export default App;